<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { UploadRequestOptions } from 'element-plus'
import { ElMessage } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import type { SiteSettingsMap } from '@/api/modules/system'
import { fetchSiteSettings, saveSiteSettings, uploadAdminSystemImage } from '@/api/modules/system'
import {
  DEFAULT_LOAN_STATUS_PRESETS,
  normalizeLoanStatusPresetRows,
  parseLoanStatusPresetsConfig,
  randomLoanStatusPresetId,
  stringifyLoanStatusPresetsConfig,
  type LoanStatusPreset,
} from '@/constants/loanStatusPresets'

const loading = ref(false)
const saving = ref(false)
const activeTab = ref(window.location.hash === '#loan-status-presets' ? 'loan-status-presets' : 'general')

const form = reactive<SiteSettingsMap>({})
const statusPresetRows = ref<LoanStatusPreset[]>([])

function syncStatusPresetRowsFromForm() {
  statusPresetRows.value = parseLoanStatusPresetsConfig(form.loan_status_presets)
}

function syncStatusPresetRowsToForm() {
  const normalized = normalizeLoanStatusPresetRows(statusPresetRows.value, DEFAULT_LOAN_STATUS_PRESETS)
  form.loan_status_presets = stringifyLoanStatusPresetsConfig(normalized)
  statusPresetRows.value = normalized
}

async function reload() {
  loading.value = true
  try {
    const { data } = await fetchSiteSettings()
    Object.assign(form, data)
    syncStatusPresetRowsFromForm()
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!String(form.sitename || '').trim() || !String(form.sitetitle || '').trim()) {
    ElMessage.warning('站点名称和站点标题不能为空')
    return
  }
  for (const key of ['yzfpay', 'minmoney', 'maxmoney', 'definamoney'] as const) {
    const raw = form[key]
    if (raw == null || String(raw).trim() === '') continue
    const t = String(raw).replace(/,/g, '').replace(/\s*(aed|usd|npr)\s*/gi, ' ').trim()
    const m = t.match(/(-?\d+(?:\.\d+)?)/)
    if (m) form[key] = m[1]
  }
  syncStatusPresetRowsToForm()
  saving.value = true
  try {
    const { data } = await saveSiteSettings({ ...form })
    if (Number(data.status) === 1) {
      ElMessage.success('保存成功')
    }
  } finally {
    saving.value = false
  }
}

function addStatusPresetRow() {
  statusPresetRows.value.push({
    id: randomLoanStatusPresetId(),
    title: '',
    status: 0,
    statusText: '',
    text: '',
    smsSuggest: '',
  })
}

function removeStatusPresetRow(i: number) {
  statusPresetRows.value.splice(i, 1)
}

function resetStatusPresetRowsToDefault() {
  statusPresetRows.value = DEFAULT_LOAN_STATUS_PRESETS.map(r => ({ ...r }))
}

async function onQrUpload(options: UploadRequestOptions) {
  try {
    const { data } = await uploadAdminSystemImage(options.file as File)
    if (Number(data.status) !== 1 || !data.url) {
      ElMessage.error(data.msg || '上传失败')
      options.onError?.(new Error('bad_response') as any)
      return
    }
    form.weixin = data.url
    ElMessage.success('二维码已上传')
    options.onSuccess!(data as any)
  } catch {
    ElMessage.error('上传失败')
    options.onError?.(new Error('network') as any)
  }
}

onMounted(reload)
</script>

<template>
  <PageChrome title="系统设置" description="与 ThinkPHP Admin System/index 一致：键入 site_kv（cfg_*）；关站、短信、放款区间、Smsbao 等由后端实时读取。">
    <template #actions>
      <el-button :loading="loading" @click="reload">重新加载</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存设置</el-button>
    </template>

    <div v-loading="loading" class="sys-body">
    <el-tabs v-model="activeTab" type="border-card" class="sys-tabs">
      <el-tab-pane label="常规与服务" name="general">
        <el-form label-width="168px" class="sys-form" size="default">
          <div class="sys-section-head">客户服务</div>
          <el-form-item label="客服链接 1">
            <el-input v-model="form.kefulj" maxlength="500" placeholder="选填；为空时门户不显示对应按钮" />
          </el-form-item>
          <el-form-item label="客服链接 2">
            <el-input v-model="form.kefulj2" maxlength="500" placeholder="选填" />
          </el-form-item>
          <el-form-item label="客服链接 3">
            <el-input v-model="form.kefulj3" maxlength="500" placeholder="选填" />
          </el-form-item>

          <div class="sys-section-head">短信平台（Smsbao）</div>
          <el-form-item label="Gwid">
            <el-input v-model="form.sms_name" maxlength="160" />
          </el-form-item>
          <el-form-item label="用户名">
            <el-input v-model="form.sms_user" maxlength="160" autocomplete="off" />
          </el-form-item>
          <el-form-item label="密码（明文，API 端转 MD5）">
            <el-input v-model="form.sms_pass" maxlength="160" type="password" show-password autocomplete="new-password" />
          </el-form-item>

          <div class="sys-section-head">站点</div>
          <el-form-item label="站点名称" required>
            <el-input v-model="form.sitename" maxlength="160" />
          </el-form-item>
          <el-form-item label="站点标题" required>
            <el-input v-model="form.sitetitle" maxlength="200" />
          </el-form-item>
          <el-form-item label="站点网址">
            <el-input v-model="form.siteurl" maxlength="500" />
          </el-form-item>
          <el-form-item label="关键词">
            <el-input v-model="form.sitekeywords" maxlength="500" />
          </el-form-item>
          <el-form-item label="描述">
            <el-input v-model="form.sitedescription" maxlength="500" />
          </el-form-item>
          <el-form-item label="每日短信上限">
            <el-input v-model="form.smsmaxcount" maxlength="20" />
          </el-form-item>
          <el-form-item label="关闭站点">
            <el-radio-group v-model="form.siteclosed">
              <el-radio label="0">否</el-radio>
              <el-radio label="1">是</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="关闭提示">
            <el-input v-model="form.siteclosemsg" type="textarea" :rows="4" />
          </el-form-item>
          <el-form-item label="ICP / 页脚文字">
            <el-input v-model="form.siteicp" maxlength="500" />
          </el-form-item>
          <el-form-item label="统计 / 第三方脚本">
            <el-input v-model="form.sitecode" type="textarea" :rows="5" placeholder="按需粘贴脚本；注意安全与合规" />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="借款参数" name="loan">
        <el-alert type="warning" show-icon class="sys-loan-tip" :closable="false">
          服务费率与"可选期数（月）"按顺序一一对应（小数形式，如 0.05=5%）；保存后门户申请、钱包、详情页将使用与订单期数匹配的费率。请先保存再重新计算月供。
        </el-alert>
        <el-form label-width="180px" class="sys-form" size="default">
          <el-form-item label="最低借款金额">
            <el-input v-model="form.minmoney" maxlength="32" />
          </el-form-item>
          <el-form-item label="最高借款金额">
            <el-input v-model="form.maxmoney" maxlength="48" />
          </el-form-item>
          <el-form-item label="默认显示金额">
            <el-input v-model="form.definamoney" maxlength="32" />
          </el-form-item>
          <el-form-item label="可选期数（月）">
            <el-input v-model="form.dkmonths" placeholder="例如 3,6,12,24,36,48" maxlength="160" />
          </el-form-item>
          <el-form-item label="默认期数（月）">
            <el-input v-model="form.definamonth" maxlength="8" />
          </el-form-item>
          <el-form-item label="服务费率序列（门户显示）">
            <el-input v-model="form.fuwufei" placeholder="例如 0.03,0.04,0.05 对应上方各期数" maxlength="500" />
          </el-form-item>
          <el-form-item label="每月还款参考日">
            <el-input v-model="form.huankuanri" maxlength="8" />
          </el-form-item>
          <el-form-item label="自动拒绝待审订单">
            <el-radio-group v-model="form.autodisdk">
              <el-radio label="0">否</el-radio>
              <el-radio label="1">是</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="自动拒绝天数">
            <el-input v-model="form.autodisdkday" maxlength="16" placeholder="PHP 端为 0 时视为 3 天" />
          </el-form-item>
          <el-form-item label="拒绝后重试间隔（天）">
            <el-input v-model="form.disdkdleyday" maxlength="16" />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="客服微信二维码" name="wechat">
        <div class="wx-wrap">
          <el-image v-if="form.weixin" :src="form.weixin" fit="contain" class="wx-preview" />
          <div v-else class="wx-placeholder">尚未配置图片 URL</div>
          <div class="wx-actions">
            <el-upload accept="image/*" :show-file-list="false" :http-request="onQrUpload">
              <template #trigger>
                <el-button type="primary" :icon="UploadFilled">上传到本地存储</el-button>
              </template>
            </el-upload>
            <el-input v-model="form.weixin" type="textarea" :rows="2" placeholder="或直接粘贴公开 URL" class="wx-url" />
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="收款账户展示" name="collection">
        <el-form label-width="180px" class="sys-form" size="default">
          <el-form-item label="收款银行名称">
            <el-input v-model="form.bank_name" maxlength="200" />
          </el-form-item>
          <el-form-item label="收款账户名">
            <el-input v-model="form.bank_names" maxlength="200" />
          </el-form-item>
          <el-form-item label="收款账号">
            <el-input
              v-model="form.bank_num"
              maxlength="80"
              placeholder="公司银行账号，对应前台 Bank account number"
            />
          </el-form-item>
          <el-form-item label="收款 IBAN">
            <el-input v-model="form.bank_iban" maxlength="42" placeholder="选填，用于跨境转账" />
          </el-form-item>
          <el-form-item label="审核费横幅 · 门户总开关">
            <el-radio-group v-model="form.portal_verify_banner">
              <el-radio label="0">关（门户不显示）</el-radio>
              <el-radio label="1">开（仅对勾选了"用户需要审核"的账号显示）</el-radio>
            </el-radio-group>
            <div style="margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.4">
              用户级开关在"前台用户管理 → 查看资料"中设置；"我的"页面的审核费入口仅在两个开关都开启时显示。
            </div>
          </el-form-item>
          <el-form-item label="资金验证费（贷款审核费）">
            <el-input v-model="form.yzfpay" maxlength="24" placeholder="纯数字，如 200（不要写 AED）" />
            <div style="margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.4">
              前台支付页"参考金额"读取此值；保存后用户重新打开页面将看到新金额。
            </div>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="订单状态模板" name="loan-status-presets">
        <el-alert type="info" show-icon class="sys-loan-tip" :closable="false">
          这里维护"信贷订单 → 修改状态"弹窗里的快捷模板。保存后，订单页点击"重载模板"或重新进入页面即可使用。
        </el-alert>
        <div class="preset-actions">
          <el-button type="primary" @click="addStatusPresetRow">添加模板</el-button>
          <el-button @click="resetStatusPresetRowsToDefault">恢复默认模板</el-button>
        </div>
        <el-table :data="statusPresetRows" border size="small" class="preset-table" max-height="560">
          <el-table-column label="显示名称" width="160">
            <template #default="{ row }">
              <el-input v-model="row.title" placeholder="例如 Bank Incorrect" />
            </template>
          </el-table-column>
          <el-table-column label="状态码" width="120" align="center">
            <template #default="{ row }">
              <el-input-number v-model="row.status" :step="1" controls-position="right" />
            </template>
          </el-table-column>
          <el-table-column label="状态文本 statusText" min-width="190">
            <template #default="{ row }">
              <el-input v-model="row.statusText" type="textarea" :rows="2" />
            </template>
          </el-table-column>
          <el-table-column label="提示正文 text" min-width="260">
            <template #default="{ row }">
              <el-input v-model="row.text" type="textarea" :rows="3" />
            </template>
          </el-table-column>
          <el-table-column label="短信内容 smsSuggest" min-width="260">
            <template #default="{ row }">
              <el-input v-model="row.smsSuggest" type="textarea" :rows="3" />
            </template>
          </el-table-column>
          <el-table-column label="" width="72" align="center">
            <template #default="{ $index }">
              <el-button type="danger" link @click="removeStatusPresetRow($index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

      <div class="sys-foot">
        <el-button type="primary" size="large" :loading="saving" @click="submit">保存设置</el-button>
      </div>
    </div>
  </PageChrome>
</template>

<style scoped lang="scss">
.sys-body {
  min-height: 200px;
}
.sys-tabs {
  border-radius: var(--ep-radius-lg);
  border: 1px solid var(--el-border-color-lighter);
  box-shadow: var(--ep-shadow-sm);
  &:deep(.el-tabs__header) {
    margin: 0;
  }
  &:deep(.el-tabs__content) {
    padding: 16px;
  }
}
.sys-section-head {
  font-weight: 700;
  margin: 8px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--el-border-color);
  color: var(--ep-text-strong);
}
.sys-form {
  max-width: 820px;
}
.sys-loan-tip {
  margin-bottom: 14px;
}
.preset-actions {
  margin-bottom: 12px;
  display: flex;
  gap: 10px;
}
.preset-table {
  width: 100%;
}
.wx-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}
.wx-preview {
  width: 200px;
  height: 202px;
  border: 1px solid #dcdfe6;
  border-radius: 10px;
  background: #fafafa;
}
.wx-placeholder {
  width: 200px;
  height: 202px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  border: 1px dashed #dcdfe6;
  border-radius: 10px;
}
.wx-actions {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.wx-url {
  max-width: 560px;
}
.sys-foot {
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
</style>
